import * as THREE from 'three';

export interface CloudData {
  mesh: THREE.Group;
  basePos: THREE.Vector3;
  speed: number;
  scale: number;
  isParting: boolean;
  partingAlpha: number;
}

export class StylizedCloudField {
  public root: THREE.Group = new THREE.Group();
  public clouds: CloudData[] = [];
  private boundX: number = 100;
  private onCloudClickedCallback?: () => void;

  constructor(count: number = 8, altitude: number = 32) {
    this.root.name = 'CloudField';

    for (let i = 0; i < count; i++) {
      const cloudGroup = this.createCloudCluster();
      const x = (Math.random() - 0.5) * this.boundX * 2;
      const y = altitude + (Math.random() - 0.5) * 8;
      const z = (Math.random() - 0.5) * 70 - 15;
      const scale = 0.8 + Math.random() * 0.7;

      cloudGroup.position.set(x, y, z);
      cloudGroup.scale.setScalar(scale);

      this.root.add(cloudGroup);
      this.clouds.push({
        mesh: cloudGroup,
        basePos: new THREE.Vector3(x, y, z),
        speed: 1.2 + Math.random() * 1.8,
        scale,
        isParting: false,
        partingAlpha: 0,
      });
    }
  }

  private createCloudCluster(): THREE.Group {
    const group = new THREE.Group();
    const sphereMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.9,
      metalness: 0.0,
      flatShading: true,
    });

    // 4-6 overlapping puff spheres
    const puffCount = 5;
    for (let i = 0; i < puffCount; i++) {
      const radius = 2.5 + Math.random() * 2.0;
      const geo = new THREE.DodecahedronGeometry(radius, 1);
      const mesh = new THREE.Mesh(geo, sphereMat);
      mesh.position.set(
        (i - 2) * 2.2 + (Math.random() - 0.5) * 1.5,
        (Math.random() - 0.5) * 1.2,
        (Math.random() - 0.5) * 2.0
      );
      mesh.castShadow = true;
      group.add(mesh);
    }

    return group;
  }

  public partNearestCloud(raycaster: THREE.Raycaster): boolean {
    const intersects = raycaster.intersectObjects(this.root.children, true);
    if (intersects.length > 0) {
      // Find parent cloud
      let target: THREE.Object3D | null = intersects[0].object;
      while (target && target.parent !== this.root) {
        target = target.parent;
      }
      if (target) {
        const cloudData = this.clouds.find(c => c.mesh === target);
        if (cloudData && !cloudData.isParting) {
          cloudData.isParting = true;
          cloudData.partingAlpha = 0;
          this.onCloudClickedCallback?.();
          return true;
        }
      }
    }
    return false;
  }

  public update(dt: number): void {
    for (const c of this.clouds) {
      if (c.isParting) {
        c.partingAlpha += dt * 1.5;
        // Expand and float upward
        c.mesh.scale.setScalar(c.scale * (1 + c.partingAlpha * 0.8));
        c.mesh.position.y += dt * 3.0;

        if (c.partingAlpha >= 1.0) {
          // Respawn at windward side
          c.isParting = false;
          c.partingAlpha = 0;
          c.mesh.position.x = -this.boundX;
          c.mesh.position.y = c.basePos.y;
          c.mesh.scale.setScalar(c.scale);
        }
      } else {
        // Natural drift
        c.mesh.position.x += c.speed * dt;
        if (c.mesh.position.x > this.boundX) {
          c.mesh.position.x = -this.boundX;
        }
      }
    }
  }

  public onCloudClicked(cb: () => void): void {
    this.onCloudClickedCallback = cb;
  }

  public dispose(): void {
    while (this.root.children.length > 0) {
      const child = this.root.children[0];
      this.root.remove(child);
    }
  }
}
